import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatError, MatFormField } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatOption, MatSelect } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltip } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { isNil, truncate } from 'lodash-es';
import { escapeHtml } from '../../utils/html';
import {
  catchError,
  EMPTY,
  filter,
  finalize,
  forkJoin,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { HasPermissionDirective, PermissionService, UserService } from '@ipg/ngx-frontend-toolset';
import { DialogService } from '../../components/dialog/dialog.service';
import { User } from '../../models/user.model';
import { UnitService } from '../units/services/unit.service';

const ZIP_CODE_REGEX: Record<string, RegExp> = {
  NL: /^[1-9][0-9]{3}\s?[a-zA-Z]{2}$/, // Dutch: 1234 AB or 1234AB
  // BE: /^[1-9][0-9]{3}$/, // Belgian: 1000–9999
  // DE: /^\d{5}$/, // German: 5 digits
};

// `country` is excluded: it defaults to NL, so it never signals user intent to
// fill in an address.
const ADDRESS_FIELDS = [
  'streetName',
  'houseNumber',
  'city',
  'zipcode',
  'province',
] as const;

const EMPTY_ADDRESS = {
  streetName: '',
  houseNumber: '',
  city: '',
  zipcode: '',
  province: '',
  country: 'NL',
};

const isBlank = (value: unknown): boolean =>
  isNil(value) || (typeof value === 'string' && value.trim() === '');

const setControlError = (
  control: AbstractControl,
  key: string,
  error: unknown | null
): void => {
  const errors = { ...(control.errors ?? {}) };

  if (isNil(error)) {
    delete errors[key];
  } else {
    errors[key] = error;
  }

  control.setErrors(Object.keys(errors).length > 0 ? errors : null);
};

// The address is optional as a whole, but the backend rejects a partial one, so
// filling in any field makes the rest required.
const addressValidator =
  (): ValidatorFn =>
  (control: AbstractControl): ValidationErrors | null => {
    const controls = ADDRESS_FIELDS.map((name) => control.get(name)).filter(
      (field): field is AbstractControl => !isNil(field)
    );

    if (controls.length !== ADDRESS_FIELDS.length) {
      return null;
    }

    const filled = controls.filter((field) => !isBlank(field.value));
    const isPartial = filled.length > 0 && filled.length < controls.length;

    for (const field of controls) {
      const isMissing = isPartial && isBlank(field.value);

      setControlError(field, 'required', isMissing ? true : null);
    }

    const zipcodeControl = control.get('zipcode')!;
    const country = control.get('country')?.value;
    const zipcode = zipcodeControl.value;
    const pattern = ZIP_CODE_REGEX[country];

    const hasInvalidZipcode =
      !isBlank(zipcode) && !!pattern && !pattern.test(zipcode);

    setControlError(
      zipcodeControl,
      'invalidZipcode',
      hasInvalidZipcode ? { country, zipcode } : null
    );

    return null;
  };

@Component({
  selector: 'app-settings-general',
  imports: [
    MatButton,
    MatFormField,
    MatInput,
    MatError,
    MatSelect,
    MatOption,
    MatTooltip,
    ReactiveFormsModule,
    HasPermissionDirective,
  ],
  templateUrl: './settings-general.component.html',
  styleUrl: './settings-general.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SettingsGeneralComponent {
  private readonly _dialog = inject(DialogService);
  private readonly _snackbar = inject(MatSnackBar);
  private readonly _fb = inject(FormBuilder);
  private readonly _router = inject(Router);
  private readonly _unitService = inject(UnitService);
  private readonly _userService = inject<UserService<User>>(UserService);
  private readonly _permissionService = inject(PermissionService);

  protected readonly form = this._fb.group({
    name: this._fb.control('', [Validators.required, Validators.maxLength(100)]),
    address: this._fb.group(
      {
        streetName: this._fb.control('', [Validators.maxLength(100)]),
        houseNumber: this._fb.control('', [Validators.maxLength(50)]),
        city: this._fb.control('', [Validators.maxLength(100)]),
        zipcode: this._fb.control(''),
        province: this._fb.control('', [Validators.maxLength(100)]),
        country: this._fb.control('NL', [Validators.required]),
      },
      { validators: addressValidator() }
    ),
  });

  protected readonly submitted = signal(false);

  protected readonly canDeleteUnit = computed(() => {
    const userData = this._userService.data();
    const unitData = this._unitService.currentUnit();

    const isRoot = userData?.rootUnitId === unitData?._id;
    const hasChildren = (unitData?.nextUnitIds.length ?? 0) > 0;

    return !isRoot && !hasChildren;
  });

  protected readonly deleteUnitTooltip = computed(() => {
    const userData = this._userService.data();
    const unitData = this._unitService.currentUnit();

    if (userData?.rootUnitId === unitData?._id) {
      return 'De root unit kan niet worden verwijderd';
    }

    if ((unitData?.nextUnitIds.length ?? 0) > 0) {
      return 'Een unit met onderliggende units kan niet worden verwijderd';
    }

    return '';
  });

  get controls() {
    return this.form.controls;
  }

  get addressControls() {
    return this.controls.address.controls;
  }

  constructor() {
    this.addressControls.country.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this.controls.address.updateValueAndValidity();
        this.addressControls.zipcode.markAsTouched();
      });

    toObservable(this._unitService.currentUnit)
      .pipe(
        takeUntilDestroyed(),
        filter((result) => !isNil(result)),
        // Patch over the empty defaults so a unit without an address clears the
        // fields of the previously active unit instead of inheriting them.
        tap((result) =>
          this.form.patchValue({
            name: result.name,
            address: { ...EMPTY_ADDRESS, ...(result.address ?? {}) },
          })
        )
      )
      .subscribe();

    if (!this._permissionService.hasPermission('unit:update')) {
      this.form.disable();
    }
  }

  handleUnitDelete() {
    const currentUnit = this._unitService.currentUnit();

    if (!currentUnit) {
      return;
    }

    const parentUnitId = currentUnit.prevUnitId;

    this._dialog.prompt({
      title: 'Unit verwijderen?',
      message: `Weet je zeker dat je de unit <b>${escapeHtml(currentUnit.name)}</b> wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.
      <br /><br />
      Als je deze unit verwijdert, worden <b>alle gegevens</b> die onder deze unit vallen permanent verwijderd, inclusief:
      <br /><br />
      <ul class="list-disc pl-6">
        <li>Alle <b>cliënten</b> en alle gegevens die aan hen zijn gekoppeld.</li>
        <li><b>Media</b> gekoppeld aan deze unit.</li>
        <li><b>Gebruikers</b> die aan deze unit zijn toegewezen.</li>
        <li>Alle <b>apparaten</b> die aan deze unit zijn gekoppeld worden losgekoppeld en teruggezet naar de voorraad.</li>
      </ul>
      <br />
      Typ <b>${escapeHtml(currentUnit.name)}</b> om te bevestigen.`,
      confirmText: 'Verwijderen',
      cancelText: 'Annuleren',
      input: {
        mode: 'input',
        placeholder: 'Naam van unit',
        validators: {
          required: {
            validator: Validators.required,
            errorMessage: `Veld is verplicht`,
          },
          pattern: {
            validator: Validators.pattern(currentUnit.name),
            errorMessage: 'Naam komt niet overeen',
          },
        },
      },
      onConfirm: (result) =>
        of(result).pipe(
          filter((result) => !isNil(result)),
          switchMap(() =>
            this._unitService.delete(currentUnit._id).pipe(
              catchError(() => {
                this._dialog.alert({
                  title: 'Foutmelding',
                  message: `Er is iets misgegaan bij het verwijderen van unit ${escapeHtml(currentUnit.name)}. Probeer het later nog eens`,
                  confirmText: 'OK',
                });

                return EMPTY;
              }),
              switchMap(() => this._unitService.activate(parentUnitId!)),
              switchMap(() =>
                forkJoin([this._unitService.getCurrent(), this._userService.fetch()])
              ),
              tap(() => {
                this._router.navigate(['clients'], { replaceUrl: true });
                this._snackbar.open(
                  `Unit ${truncate(currentUnit.name)} ingepland voor verwijdering`,
                  'OK'
                );
              })
            )
          )
        ),
    });
  }

  handleSubmit() {
    this.submitted.set(true);

    const currentUnit = this._unitService.currentUnit();

    if (!currentUnit) {
      return;
    }

    const { name, address } = this.form.getRawValue();

    const hasAddress = ADDRESS_FIELDS.some((field) => !isBlank(address[field]));

    this._unitService
      .update(currentUnit._id, { name, address: hasAddress ? address : null })
      .pipe(
        catchError(() => {
          this._dialog.alert({
            title: 'Foutmelding',
            message:
              'Er is iets misgegaan bij het opslaan van de instellingen. Probeer het later nog eens',
            confirmText: 'OK',
          });

          return EMPTY;
        }),
        switchMap(() => this._unitService.getCurrent()),

        finalize(() => this.submitted.set(false))
      )
      .subscribe({
        next: () => this._snackbar.open('Instellingen opgeslagen'),
      });
  }
}
